import React from 'react';
import { X, FileText, Receipt, Package as PackageIcon, BadgePercent, Camera, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { exportSinglePDF } from '../../lib/exporters';
import { getSalesForClient, getPackagesForClient } from '../../lib/salesStorage';
import { money } from '../../lib/services';
import PhotoGallery from '../../components/PhotoGallery';
import SessionNotesList from '../../components/SessionNotesList';

export default function ClientDetailModal({ waiver, onClose }) {
  if (!waiver) return null;

  const sales = getSalesForClient(waiver.id);
  const packages = getPackagesForClient(waiver.id);
  const lifetime = sales.reduce((s, x) => s + x.total, 0);
  const activePackages = packages.filter((p) => p.sessionsRemaining > 0);

  return (
    <div
      className="fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-view-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-8 py-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-blue-400 font-bold">
              Client Record
            </p>
            <h3 className="font-display text-2xl text-slate-100 mt-1">
              {waiver.name}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="pill-btn h-11 w-11 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-200 inline-flex items-center justify-center"
            data-testid="admin-view-close-button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-8 py-6 space-y-6">
          {/* Contact info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Email</p>
              <p className="text-slate-100 mt-1 break-all">{waiver.email}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Phone</p>
              <p className="text-slate-100 mt-1">{waiver.phone}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Waiver date</p>
              <p className="text-slate-100 mt-1">{waiver.date}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Signed at</p>
              <p className="text-slate-100 mt-1">{new Date(waiver.signedAt).toLocaleString()}</p>
            </div>
          </div>

          {/* Lifetime summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs uppercase tracking-widest text-blue-300 font-bold flex items-center gap-1.5">
                <Receipt className="w-3.5 h-3.5" />
                Sales
              </p>
              <p className="font-display text-2xl text-white mt-1" data-testid="client-sales-count">
                {sales.length}
              </p>
            </div>
            <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs uppercase tracking-widest text-blue-300 font-bold">Lifetime spend</p>
              <p className="font-display text-2xl text-white mt-1" data-testid="client-lifetime-spend">
                {money(lifetime)}
              </p>
            </div>
            <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs uppercase tracking-widest text-blue-300 font-bold flex items-center gap-1.5">
                <PackageIcon className="w-3.5 h-3.5" />
                Active packages
              </p>
              <p className="font-display text-2xl text-white mt-1" data-testid="client-active-packages">
                {activePackages.length}
              </p>
            </div>
          </div>

          {/* Active packages */}
          {activePackages.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Active packages</p>
              <div className="space-y-2" data-testid="client-active-packages-list">
                {activePackages.map((p) => (
                  <div
                    key={p.id}
                    className="p-4 rounded-2xl border border-blue-500/30 bg-blue-500/10/30 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white">{p.serviceName}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {p.sessionsPurchased}-pack · Paid {money(p.totalPaid)} (−{p.discountPct}%)
                      </p>
                    </div>
                    <p className="font-display text-lg text-white shrink-0">
                      <span className="text-2xl">{p.sessionsRemaining}</span>
                      <span className="text-slate-400"> / {p.sessionsPurchased}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Purchase history */}
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Purchase history</p>
            {sales.length === 0 ? (
              <p className="text-sm text-slate-400 italic py-3">
                No purchases yet for this client.
              </p>
            ) : (
              <div className="space-y-2" data-testid="client-purchase-history">
                {sales.map((s) => (
                  <div key={s.id} className="p-4 rounded-2xl border border-white/10 bg-white">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs text-slate-400">
                          {new Date(s.createdAt).toLocaleString()}
                        </p>
                        <p className="text-sm text-slate-100 mt-1">
                          {s.items.map((it, i) => (
                            <span key={i}>
                              {it.serviceName} <span className="text-slate-400">×{it.sessions}</span>
                              {i < s.items.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </p>
                        {(s.bundleSavings > 0 || s.packageDiscount > 0) && (
                          <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase font-bold text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-full px-2 py-0.5">
                            <BadgePercent className="w-3 h-3" />
                            Saved {money(s.bundleSavings + s.packageDiscount)}
                          </p>
                        )}
                      </div>
                      <p className="font-display text-lg text-white shrink-0">
                        {money(s.total)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Session Notes (staff editable) */}
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2 flex items-center gap-2">
              <StickyNote className="w-3.5 h-3.5" />
              Session notes
            </p>
            <SessionNotesList
              clientId={waiver.id}
              canEdit={true}
              testIdPrefix="admin-note"
            />
          </div>

          {/* Photos (staff editable) */}
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2 flex items-center gap-2">
              <Camera className="w-3.5 h-3.5" />
              Before / During / After photos
            </p>
            <PhotoGallery
              clientId={waiver.id}
              uploadedBy="staff"
              canEdit={true}
              testIdPrefix="admin-photo"
            />
          </div>

          {/* Signature */}
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Signature</p>
            <div className="rounded-2xl border border-blue-500/30 bg-blue-500/10/40 p-3">
              <img
                src={waiver.signature}
                alt={`Signature of ${waiver.name}`}
                className="w-full h-40 object-contain bg-white rounded-xl"
                data-testid="admin-view-signature-image"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={() => {
                exportSinglePDF(waiver);
                toast.success('PDF exported.');
              }}
              className="pill-btn inline-flex items-center gap-2 h-11 px-5 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-sm font-semibold"
              data-testid="admin-view-export-pdf-button"
            >
              <FileText className="w-4 h-4" />
              Export waiver as PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
